import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, type UserRole } from '@/store/auth';

/**
 * Rewrites a path between role-shells when a user lands in the wrong area.
 * Example: lawyer hits `/app/tarefas/X` → `/lawyer/tarefas/X` (preserves
 * sub-paths so deep-links from email survive the redirect). Falls back to
 * the role's dashboard root if there's no `/app` or `/lawyer` prefix to swap.
 */
function rewriteForRole(pathname: string, search: string, targetRole: UserRole): string {
  const targetPrefix = targetRole === 'lawyers' ? '/lawyer' : targetRole === 'admins' ? '/admin' : '/app';
  const otherPrefixes = ['/app', '/lawyer', '/admin'].filter((p) => p !== targetPrefix);
  for (const fromPrefix of otherPrefixes) {
    if (pathname === fromPrefix || pathname.startsWith(`${fromPrefix}/`)) {
      return `${pathname.replace(fromPrefix, targetPrefix)}${search}`;
    }
  }
  return targetPrefix;
}

export function ProtectedRoute({ role }: { role: UserRole }) {
  const user = useAuthStore((s) => s.user);
  const tokens = useAuthStore((s) => s.tokens);
  const hydrated = useAuthStore((s) => s.hydrated);
  const location = useLocation();

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Carregando…
      </div>
    );
  }

  if (!tokens || !user) {
    // Preserve full URL (path + query) so the post-login redirect lands the user
    // exactly where they came from — e.g. ?call=:id email deep-links survive.
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  // Admins have full access to any role-gated route.
  if (user.role === 'admins') return <Outlet />;

  // Role guard: if mismatch, rewrite path into the user's actual shell rather
  // than dropping them at root. This keeps email deep-links functional regardless
  // of which session the recipient happens to be logged into.
  if (role !== user.role) {
    return <Navigate to={rewriteForRole(location.pathname, location.search, user.role)} replace />;
  }
  return <Outlet />;
}
